import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, PauseCircle, Wrench } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Lightbox from '@/components/ui/Lightbox';
import Spinner from '@/components/ui/Spinner';
import CandidateCarousel from '@/components/production/gates/CandidateCarousel';
import CrewPlanGate from '@/components/production/gates/CrewPlanGate';
import FlaggableAssetGate, { type Marks } from '@/components/production/gates/FlaggableAssetGate';
import PaperCutGate from '@/components/production/gates/PaperCutGate';
import { useReviewGate, type GateAsset } from '@/hooks/useReviewGate';
import { gateLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';

/**
 * Image design gates. Flagging one here regenerates it with the producer's note and
 * re-opens this review — the direct path that needs no chat round-trip. (Before, the
 * only route was chat → confirm → "Apply N changes"; a note typed but never confirmed
 * left the banner showing only "Approve" and the run advanced with nothing rejected.)
 */
const IMG_GATES = ['storyboard', 'characters', 'locations', 'props'] as const;

/** Gates that share the flag-and-note interaction; wording comes from the catalogue. */
const FLAG_GATES = ['footage', 'reframe', 'clips', 'voice', 'shoot_blocked', 'voice_policy', ...IMG_GATES] as const;

/** The four image gates share one wording set rather than four identical copies. */
const wordingFor = (phase: string) => ((IMG_GATES as readonly string[]).includes(phase) ? 'image' : phase);

/**
 * The human-in-the-loop gate banner.
 *
 * A paused production waits here for up to 12 hours, so this is deliberately the
 * loudest element on the console and is never collapsed behind a tab. Each phase
 * renders its own review UI below the banner; approving records the phase so it is
 * never asked again.
 */
export default function PhaseApproval({
  productionId,
  running,
  vertical,
}: {
  productionId: string;
  running: boolean;
  vertical?: boolean;
}) {
  const { t } = useTranslation(['production', 'common']);
  const { gate, flags, busy, resolve } = useReviewGate(productionId, running);
  const [marks, setMarks] = useState<Marks>({});
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [enlarged, setEnlarged] = useState<string | null>(null);

  if (!gate) return null;

  const flagPhase = (FLAG_GATES as readonly string[]).includes(gate.phase) ? gate.phase : null;
  const isImgGate = (IMG_GATES as readonly string[]).includes(gate.phase);
  const wording = flagPhase ? wordingFor(flagPhase) : null;
  const markedIds = Object.entries(marks)
    .filter(([, m]) => m.on)
    .map(([id]) => id);
  const nMarked = markedIds.length;

  /**
   * Cache-bust on the gate revision so a reworked frame refetches. The engine caches
   * full-res assets for an hour and ignores the query string, so changing the URL is
   * the only bust that works.
   */
  const withRev = (url: string) => (gate.revision ? `${url}${url.includes('?') ? '&' : '?'}rev=${gate.revision}` : url);

  /** Resolve with the accumulated flag batch and/or this gate's own marks. */
  const submit = (applyFlags: boolean) => {
    const rejected = [...new Set([...(applyFlags ? flags.map((f) => f.assetId) : []), ...markedIds])];
    const rejectedSet = new Set(rejected);
    const feedback: Record<string, string> = {};
    if (applyFlags) for (const f of flags) feedback[f.assetId] = f.note;
    for (const id of markedIds)
      feedback[id] = marks[id]?.note.trim() || (wording ? t(`gate.${wording}.default`) : 'Needs rework');

    // Only send picks that differ from the AI's default, and never for an asset
    // about to be regenerated.
    const picks: Record<string, number> = {};
    for (const a of gate.assets) {
      const sel = selections[a.id];
      if (sel !== undefined && sel !== a.chosen_index && !rejectedSet.has(a.id)) picks[a.id] = sel;
    }
    resolve({ rejectedIds: rejected, feedback, selections: picks });
    setMarks({});
    setSelections({});
  };

  /** Reject a set of assets outright with one note each (crew plan / paper cut). */
  const rejectWith = (ids: string[], note: string) => {
    if (!ids.length || !note) return;
    resolve({ rejectedIds: ids, feedback: Object.fromEntries(ids.map((id) => [id, note])) });
  };

  const allIds = gate.assets.map((a) => a.id);
  const withCandidates = gate.assets.filter((a) => (a.candidates?.length ?? 0) > 1);

  const media = (a: GateAsset): React.ReactNode => {
    if (gate.phase === 'reframe') {
      return a.path ? (
        <video src={a.path} controls preload="metadata" className="aspect-[9/16] w-full bg-black object-contain" />
      ) : (
        <div className="flex aspect-[9/16] w-full items-center justify-center bg-muted text-[10px] text-faint">
          {t('state.noPreview', { ns: 'common' })}
        </div>
      );
    }
    if (gate.phase === 'clips') {
      // Each take before it goes into the edit. The gate revision busts the cache so a
      // re-shot clip plays its new take.
      return a.path ? (
        <video
          src={withRev(a.path)}
          controls
          preload="metadata"
          className={cn('w-full bg-black object-contain', vertical ? 'aspect-[9/16]' : 'aspect-video')}
        />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center bg-muted text-[10px] text-faint">
          {t('state.noPreview', { ns: 'common' })}
        </div>
      );
    }
    if (gate.phase === 'shoot_blocked') {
      // The shot's storyboard frame (if it has one) and WHY it can't be generated.
      return (
        <div>
          {a.path ? (
            <img src={withRev(a.path)} alt={a.id} className="aspect-video w-full object-cover" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center bg-muted text-[10px] text-faint">
              {t('state.noPreview', { ns: 'common' })}
            </div>
          )}
          {a.reason && <p className="px-1.5 pt-1.5 text-[11px] leading-snug text-muted-foreground">{a.reason}</p>}
        </div>
      );
    }
    if (gate.phase === 'voice_policy') {
      // A scripted line the brief's voice policy forbids: who says it and what.
      return (
        <div className="px-2 pt-2 text-[12px] leading-snug">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-faint">
            {a.speaker || '—'} · {a.reason}
          </div>
          <p className="mt-1 text-foreground">“{a.text}”</p>
        </div>
      );
    }
    if (gate.phase === 'voice') {
      const src = a.path ? `/api/assets/${productionId}/${a.path}` : '';
      return src ? (
        <audio src={src} controls preload="none" className="h-8 w-full px-1.5 pt-1.5" />
      ) : (
        <div className="px-1.5 pt-1.5 text-[10px] text-faint">{t('state.noPreview', { ns: 'common' })}</div>
      );
    }
    // Image gates: click to inspect full-size before deciding — a w-32 thumbnail is
    // too small to judge a face or a set on.
    return a.path ? (
      <img
        src={withRev(a.path)}
        alt={a.id}
        className={cn('aspect-video w-full object-cover transition-all', isImgGate && 'cursor-zoom-in')}
        onClick={isImgGate && a.path ? () => setEnlarged(withRev(a.path as string)) : undefined}
        onError={(e) => (e.currentTarget.style.display = 'none')}
      />
    ) : (
      <div className="flex aspect-video w-full items-center justify-center bg-muted text-[10px] text-faint">
        {t('state.noPreview', { ns: 'common' })}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'rounded-xl border-2 p-3.5',
        gate.stalled ? 'border-destructive/70 bg-destructive/10' : 'border-primary/60 bg-primary/10',
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <PauseCircle className={cn('h-6 w-6 shrink-0', gate.stalled ? 'text-destructive' : 'text-primary')} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">
            {gate.stalled
              ? t('gate.heldTitle', { what: gateLabel(t, gate.phase) })
              : t('gate.pausedTitle', { what: gateLabel(t, gate.phase) })}
            <Badge variant="outline" className="ml-2 text-xs">
              {t('gate.assetCount', { count: gate.assets.length })}
            </Badge>
            {gate.stalled && (
              <Badge variant="destructive" className="ml-1 text-xs">
                {t('gate.notConverging')}
              </Badge>
            )}
            {flags.length > 0 && <Badge className="ml-1 text-xs">{t('gate.flaggedCount', { count: flags.length })}</Badge>}
          </div>
          <div className="text-xs leading-relaxed text-muted-foreground">
            {gate.stalled
              ? t('gate.stalledHint')
              : flags.length > 0
                ? t('gate.flagsWaiting', { count: flags.length })
                : t(`gate.hint.${gate.phase}`, { defaultValue: t('gate.defaultHint') })}
          </div>
        </div>
        {flags.length > 0 && (
          <Button onClick={() => submit(true)} disabled={busy} variant="secondary" className="shrink-0">
            {busy ? <Spinner className="mr-1" /> : <Wrench className="mr-1 h-4 w-4" />}
            {t('gate.applyChanges', { count: flags.length })}
          </Button>
        )}
        <Button onClick={() => submit(false)} disabled={busy} className="shrink-0">
          {busy ? <Spinner className="mr-1" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
          {nMarked > 0 && wording
            ? t(isImgGate || gate.phase === 'clips' ? 'gate.actAndReReview' : 'gate.actAndContinue', {
                verb: t(`gate.${wording}.verb`),
                count: nMarked,
              })
            : t('gate.approve')}
        </Button>
      </div>

      {gate.phase === 'crew_plan' && (
        <CrewPlanGate productionId={productionId} busy={busy} onRequestChanges={(note) => rejectWith(allIds, note)} />
      )}

      {gate.phase === 'papercut' && (
        <PaperCutGate
          productionId={productionId}
          assets={gate.assets}
          busy={busy}
          onSectionChanges={(notes) => {
            const ids = Object.entries(notes)
              .filter(([, v]) => v.trim())
              .map(([id]) => id);
            resolve({
              rejectedIds: ids,
              feedback: Object.fromEntries(ids.map((id) => [id, notes[id].trim()])),
            });
          }}
          onRejectAll={(note) => rejectWith(allIds, note)}
        />
      )}

      {flagPhase && wording && (
        <FlaggableAssetGate
          assets={gate.assets}
          marks={marks}
          onChange={setMarks}
          hint={isImgGate ? t('gate.image.hint') : t(`gate.hint.${flagPhase}`, { defaultValue: t('gate.defaultHint') })}
          onLabel={t(`gate.${wording}.on`)}
          offLabel={t(`gate.${wording}.off`)}
          notePlaceholder={t(`gate.${wording}.note`)}
          accent={gate.phase === 'footage' ? 'red' : 'amber'}
          toggleOnMedia={gate.phase === 'footage'}
          columns={gate.phase === 'voice' || gate.phase === 'voice_policy' ? 'grid-cols-1 sm:grid-cols-2' : undefined}
          renderMedia={media}
        />
      )}

      <CandidateCarousel
        productionId={productionId}
        assets={withCandidates}
        selections={selections}
        onSelect={(assetId, index) => setSelections((s) => ({ ...s, [assetId]: index }))}
        onEnlarge={setEnlarged}
        vertical={vertical}
        revision={gate.revision}
      />

      {enlarged && (
        <Lightbox onClose={() => setEnlarged(null)}>
          {/\.(mp4|mov|webm)(\?|$)/i.test(enlarged) ? (
            <video src={enlarged} controls autoPlay className="mx-auto max-h-[80vh] rounded object-contain" />
          ) : (
            <img src={enlarged} alt={t('a11y.enlarged')} className="mx-auto max-h-[80vh] rounded object-contain" />
          )}
        </Lightbox>
      )}
    </div>
  );
}
